import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCreateModelMutation, useListFilesQuery } from "store/queries/chat";
import styled from "styled-components";
import { ICreateModelRequest, IOpenAiChatModel } from "types/Chat";

import {
  MAX_SUGGESTED_PROMPTS,
  prepareModelConfig,
  SuggestedQuestionsField,
} from "./components/SuggestedQuestionsField";

const FormContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const FormField = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

export function CreateModelPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { current: toastElement } = useContext(ToastContext);
  const [createModel, { isLoading }] = useCreateModelMutation();
  const { data: filesData } = useListFilesQuery();

  // Check if we're copying from an existing model
  const copyFromModel = location.state?.copyFromModel as
    | IOpenAiChatModel
    | undefined;
  const isCopying = !!copyFromModel;

  const [formData, setFormData] = useState<ICreateModelRequest>({
    type: "PatientChatBot",
    name: "",
    identity: "",
    guidelines: "",
    examples: "",
    config: { files: [] },
    multipleChatsSupported: true,
  });

  const [customFileSelections, setCustomFileSelections] = useState<string[]>(
    []
  );

  // CLE-2811 — suggestion chips, edited as one-per-line text and stored as config.suggestedPrompts.
  // Kept as raw text in state so a half-typed line is not destroyed while typing; it is trimmed,
  // de-blanked and capped only on the way into the config.
  const [suggestedPromptsText, setSuggestedPromptsText] = useState("");

  // Pre-populate form when copying a model
  useEffect(() => {
    if (copyFromModel) {
      setFormData({
        type: copyFromModel.type || "PatientChatBot",
        name: `${copyFromModel.name} - Copy`,
        identity: copyFromModel.identity,
        guidelines: copyFromModel.guidelines,
        examples: copyFromModel.examples,
        config: copyFromModel.config || { files: [] },
        multipleChatsSupported: copyFromModel.multipleChatsSupported ?? true,
      });

      // Set custom file selections if they exist
      const customFiles = copyFromModel.config?.files || [];
      setCustomFileSelections(customFiles);

      // CLE-2811 — a copy carries the source model's suggestion chips over with it.
      setSuggestedPromptsText(
        (copyFromModel.config?.suggestedPrompts || []).join("\n")
      );
    }
  }, [copyFromModel]);

  // Get the most recent guide files for display info
  const mostRecentEligibilityFile = filesData?.db
    ?.filter((file) => file.identifier === "EligibilityProcessingGuide")
    ?.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )[0];

  const mostRecentPricingFile = filesData?.db
    ?.filter((file) => file.identifier === "PricingAdjustmentsGuide")
    ?.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )[0];

  const mostRecentContractManagementFile = filesData?.db
    ?.filter((file) => file.identifier === "ContractManagementGuide")
    ?.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )[0];

  const customFileOptions =
    filesData?.db
      ?.filter((file) => file.identifier === "Custom")
      ?.map((file) => ({
        label: `${file.name} (${new Date(
          file.createdAt
        ).toLocaleDateString()})`,
        value: file.fileId,
      })) || [];

  const typeOptions = [
    { label: "Patient Chat Bot", value: "PatientChatBot" },
    { label: "Contract Management Bot", value: "ContractManagementBot" },
  ];

  const updateSuggestedPrompts = (text: string) => {
    setSuggestedPromptsText(text);
    setFormData((previous) => ({
      ...previous,
      config: {
        ...previous.config,
        suggestedPrompts: text
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean)
          .slice(0, MAX_SUGGESTED_PROMPTS),
      },
    }));
  };

  const updateConfigFiles = (selections: string[]) => {
    const validFileIds = selections.filter(Boolean);
    setFormData({
      ...formData,
      // Spread the existing config: overwriting it wholesale would silently drop
      // config.suggestedPrompts every time a file dropdown changed (CLE-2811).
      config: { ...formData.config, files: validFileIds },
    });
  };

  const addCustomFileSelection = () => {
    if (customFileSelections.length < 10) {
      setCustomFileSelections([...customFileSelections, ""]);
    }
  };

  const removeCustomFileSelection = (index: number) => {
    const newSelections = customFileSelections.filter((_, i) => i !== index);
    setCustomFileSelections(newSelections);
    updateConfigFiles(newSelections);
  };

  const updateCustomFileSelection = (index: number, fileId: string) => {
    const newSelections = [...customFileSelections];
    newSelections[index] = fileId;
    setCustomFileSelections(newSelections);
    updateConfigFiles(newSelections);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await createModel({
        ...formData,
        // CLE-2811 — questions typed while the type was Contract Management Bot must not be
        // saved onto a patient model, which can never show them.
        config: prepareModelConfig(formData.type, formData.config),
      }).unwrap();

      toastElement?.show({
        severity: "success",
        summary: "Success!",
        detail: isCopying
          ? "Model copied successfully."
          : "Model created successfully.",
      });

      navigate("/chatbot/models");
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: error?.data?.message || "Failed to create model.",
      });
    }
  };

  const handleCancel = () => {
    navigate("/chatbot/models");
  };

  return (
    <FormContainer>
      <Card
        title={
          isCopying ? "Copy OpenAI Chat Model" : "Create New OpenAI Chat Model"
        }
      >
        <form onSubmit={handleSubmit}>
          <FormField>
            <Label htmlFor="type">Model Type *</Label>
            <Dropdown
              id="type"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.value })}
              options={typeOptions}
              placeholder="Select a model type"
              className="w-full"
              showClear
            />
          </FormField>

          <FormField>
            <Label htmlFor="name">Model Name *</Label>
            <InputText
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              required
              className="w-full"
              placeholder="Enter model name"
            />
          </FormField>

          <FormField>
            <Label htmlFor="identity">Identity *</Label>
            <InputTextarea
              id="identity"
              value={formData.identity}
              onChange={(e) =>
                setFormData({ ...formData, identity: e.target.value })
              }
              required
              rows={4}
              className="w-full"
              placeholder="Define the chatbot's role and identity"
            />
          </FormField>

          <FormField>
            <Label htmlFor="guidelines">Guidelines *</Label>
            <InputTextarea
              id="guidelines"
              value={formData.guidelines}
              onChange={(e) =>
                setFormData({ ...formData, guidelines: e.target.value })
              }
              required
              rows={10}
              className="w-full"
              placeholder="Enter operational guidelines"
            />
          </FormField>

          <FormField>
            <Label htmlFor="examples">Examples *</Label>
            <InputTextarea
              id="examples"
              value={formData.examples}
              onChange={(e) =>
                setFormData({ ...formData, examples: e.target.value })
              }
              required
              rows={12}
              className="w-full"
              placeholder="Provide example interactions"
            />
          </FormField>

          <FormField>
            <Label htmlFor="multipleChatsSupported">
              Multiple Chats Supported
            </Label>
            <Dropdown
              id="multipleChatsSupported"
              value={formData.multipleChatsSupported}
              onChange={(e) =>
                setFormData({ ...formData, multipleChatsSupported: e.value })
              }
              options={[
                { label: "Yes", value: true },
                { label: "No", value: false },
              ]}
              placeholder="Select whether multiple chats are supported"
              className="w-full"
            />
            <small className="text-muted">
              If enabled, users can create multiple chat sessions and see a
              sidebar with chat history. If disabled, only one chat session is
              supported.
            </small>
          </FormField>

          {formData.type === "PatientChatBot" && (
            <FormField>
              <Label>Auto-Included Files for Patient Chat Bot</Label>
              <div
                style={{
                  padding: "12px",
                  backgroundColor: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: "4px",
                  color: "#166534",
                }}
              >
                <strong>
                  The following files will be automatically included:
                </strong>
                <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                  <li>
                    <strong>Eligibility Processing Guide:</strong>{" "}
                    {mostRecentEligibilityFile ? (
                      <>
                        {mostRecentEligibilityFile.name} (
                        {new Date(
                          mostRecentEligibilityFile.createdAt
                        ).toLocaleDateString()}
                        )
                      </>
                    ) : (
                      "No eligibility guide found"
                    )}
                  </li>
                  <li>
                    <strong>Pricing Adjustments Guide:</strong>{" "}
                    {mostRecentPricingFile ? (
                      <>
                        {mostRecentPricingFile.name} (
                        {new Date(
                          mostRecentPricingFile.createdAt
                        ).toLocaleDateString()}
                        )
                      </>
                    ) : (
                      "No pricing guide found"
                    )}
                  </li>
                </ul>
                <small>
                  These are the most recent versions and will always be included
                  automatically for patient chat bots. You can still add
                  additional custom files below.
                </small>
              </div>
            </FormField>
          )}

          {formData.type === "ContractManagementBot" && (
            <FormField>
              <Label>Auto-Included Files for Contract Management Bot</Label>
              <div
                style={{
                  padding: "12px",
                  backgroundColor: "#f0f9ff",
                  border: "1px solid #bae6fd",
                  borderRadius: "4px",
                  color: "#0c4a6e",
                }}
              >
                <strong>
                  The following files will be automatically included:
                </strong>
                <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                  <li>
                    <strong>Contract Management Guide:</strong>{" "}
                    {mostRecentContractManagementFile ? (
                      <>
                        {mostRecentContractManagementFile.name} (
                        {new Date(
                          mostRecentContractManagementFile.createdAt
                        ).toLocaleDateString()}
                        )
                      </>
                    ) : (
                      "No contract management guide found"
                    )}
                  </li>
                </ul>
                <small>
                  This is the most recent version and will always be included
                  automatically for contract management bots. You can still add
                  additional custom files below.
                </small>
              </div>
            </FormField>
          )}

          <SuggestedQuestionsField
            modelType={formData.type}
            value={suggestedPromptsText}
            onChange={updateSuggestedPrompts}
          />

          <FormField>
            <Label>Custom Files</Label>
            {customFileOptions.length === 0 ? (
              <div
                style={{
                  padding: "12px",
                  backgroundColor: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  borderRadius: "4px",
                  color: "#1d4ed8",
                }}
              >
                <strong>No custom files available.</strong> You need to upload
                custom files before you can attach them to a model.{" "}
                <Button
                  label="Upload Custom File"
                  className="p-button-link p-0"
                  style={{ color: "#1d4ed8", textDecoration: "underline" }}
                  onClick={() => navigate("/chatbot/files/upload")}
                />
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <Button
                    type="button"
                    icon="pi pi-plus"
                    label="Add Custom File"
                    className="p-button-outlined p-button-sm"
                    onClick={addCustomFileSelection}
                    disabled={customFileSelections.length >= 10}
                  />
                </div>
                {customFileSelections.map((fileId, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      gap: "8px",
                      marginBottom: "8px",
                      alignItems: "center",
                    }}
                  >
                    <Dropdown
                      value={fileId}
                      onChange={(e) =>
                        updateCustomFileSelection(index, e.value)
                      }
                      options={customFileOptions}
                      placeholder="Select a custom file"
                      className="w-full"
                      showClear
                    />
                    <Button
                      type="button"
                      icon="pi pi-times"
                      className="p-button-danger p-button-outlined p-button-sm"
                      onClick={() => removeCustomFileSelection(index)}
                    />
                  </div>
                ))}
                {customFileSelections.length === 0 && (
                  <p className="text-muted" style={{ fontSize: "14px" }}>
                    No custom files added. Click &quot;Add Custom File&quot; to
                    include additional files in this model.
                  </p>
                )}
              </>
            )}
          </FormField>

          <ButtonContainer>
            <Button
              type="button"
              label="Cancel"
              className="p-button-outlined"
              onClick={handleCancel}
            />
            <Button
              type="submit"
              label={isCopying ? "Copy Model" : "Create Model"}
              className="p-button-raised"
              loading={isLoading}
              disabled={
                !formData.name ||
                !formData.identity ||
                !formData.guidelines ||
                !formData.examples
              }
            />
          </ButtonContainer>
        </form>
      </Card>
    </FormContainer>
  );
}
